import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { apiService } from "@/services/api";
import { router } from "@/router";
import { ADMIN_ROLES, LOGOUT_URL, REFRESH_TOKEN_URL, TOKEN_REFRESH_BUFFER_SECONDS } from "@/constants/auth";
import { parseJwtPayload, getTokenRemainingMs } from "@/utils/tokenUtils";

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  userId: number;
  email: string;
  name: string;
  phoneNumber: string;
  role: string;
  accessToken: string;
  accessTokenExpiresDate: string;
  refreshToken: string;
  refreshTokenExpiresDate: string;
}

export interface AuthUser {
  userId: number;
  email: string;
  name: string;
  phoneNumber: string;
  role: string;
}

interface TokenReissueData {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresDate?: string;
  refreshTokenExpiresDate?: string;
}

export const useAuthStore = defineStore("auth", () => {
  // Access Token: 메모리 전용 (XSS 방지 - localStorage에 저장하지 않음)
  const accessToken = ref<string | null>(null);
  // Refresh Token: localStorage 유지 (백엔드 HttpOnly 쿠키 미지원)
  const refreshToken = ref<string | null>(localStorage.getItem("refreshToken"));
  const user = ref<AuthUser | null>(null);
  const isLoading = ref(false);

  const authResolved = ref(false);
  const isLoggingOut = ref(false);

  const isAuthenticated = computed(() => !!accessToken.value);
  const isAdmin = computed(() => !!user.value?.role && ADMIN_ROLES.includes(user.value.role));
  const isPartner = computed(() => user.value?.role === "USER");

  // 선제적 토큰 갱신 타이머
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;

  const scheduleTokenRefresh = () => {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
    if (!accessToken.value) return;

    const remainingMs = getTokenRemainingMs(accessToken.value);
    const bufferMs = TOKEN_REFRESH_BUFFER_SECONDS * 1000;

    // 만료 임박(bufferMs 이내)이면 즉시 갱신
    if (remainingMs <= bufferMs && remainingMs > 0) {
      refreshTokenSilently();
      return;
    }
    if (remainingMs <= 0) return;

    const delay = remainingMs - bufferMs;
    if (import.meta.env.DEV) {
      console.log(`[Token Refresh] ${Math.round(delay / 1000)}초 후 선제적 갱신 예정`);
    }
    refreshTimer = setTimeout(async () => {
      try {
        await refreshTokenSilently();
        if (import.meta.env.DEV) {
          console.log("[Token Refresh] 선제적 토큰 갱신 완료");
        }
      } catch {
        // 선제적 갱신 실패 → 다음 요청의 401 핸들러가 처리
      }
    }, delay);
  };

  const refreshTokenSilently = async (): Promise<boolean> => {
    const savedRefreshToken = localStorage.getItem("refreshToken");
    if (!savedRefreshToken) return false;

    try {
      const response = await apiService.post<TokenReissueData>(
        REFRESH_TOKEN_URL,
        { refreshToken: savedRefreshToken },
        { _silentRefresh: true }
      );
      if (response.success) {
        setTokens(response.data.accessToken, response.data.refreshToken);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const setAccessToken = (token: string | null) => {
    accessToken.value = token;
  };

  /**
   * 토큰 쌍 설정 (interceptor refresh 성공 시 사용)
   * Pinia ref + localStorage 동기화
   */
  const setTokens = (newAccessToken: string, newRefreshToken: string) => {
    accessToken.value = newAccessToken;
    refreshToken.value = newRefreshToken;
    localStorage.setItem("refreshToken", newRefreshToken);
    scheduleTokenRefresh();
  };

  const login = async (credentials: LoginRequest) => {
    try {
      isLoading.value = true;
      const response = await apiService.post<LoginResponse>("/api/v1/auth/login", credentials);

      if (response.success) {
        setTokens(response.data.accessToken, response.data.refreshToken);

        user.value = {
          userId: response.data.userId,
          email: response.data.email,
          name: response.data.name,
          phoneNumber: response.data.phoneNumber,
          role: response.data.role
        };

        // 세션 복원 시 탭 간 공유를 위해 localStorage에 캐시
        localStorage.setItem("userName", response.data.name);
        localStorage.setItem("userPhone", response.data.phoneNumber);

        authResolved.value = true;

        return response;
      }

      throw new Error(response.message || "Login failed");
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 세션 복원 (서버 토큰 재발급 호출)
   * @returns true: 세션 복원 성공, false: 세션 없음 또는 복원 실패
   */
  const checkAuthStatus = async (): Promise<boolean> => {
    const savedRefreshToken = localStorage.getItem("refreshToken");

    if (!savedRefreshToken) {
      authResolved.value = true;
      return false;
    }

    try {
      const response = await apiService.post<TokenReissueData>(
        REFRESH_TOKEN_URL,
        { refreshToken: savedRefreshToken },
        { _silentRefresh: true }
      );

      if (response.success) {
        setTokens(response.data.accessToken, response.data.refreshToken);

        // JWT payload에서 user 정보 복원, name/phone은 로그인 시 캐시한 localStorage 값 사용
        const payload = parseJwtPayload(response.data.accessToken);
        if (payload) {
          user.value = {
            userId: Number(payload.sub),
            email: payload.email,
            name: localStorage.getItem("userName") || "",
            phoneNumber: localStorage.getItem("userPhone") || "",
            role: payload.role
          };
        }

        authResolved.value = true;
        return true;
      }

      try {
        clearSessionState();
      } finally {
        authResolved.value = true;
      }
      return false;
    } catch (error: any) {
      if (error?.errorCode === "NETWORK_ERROR") {
        authResolved.value = true;
        return false;
      }
      try {
        clearSessionState();
      } finally {
        authResolved.value = true;
      }
      return false;
    }
  };

  /**
   * 라우터 가드에서 호출 — 최초 1회만 checkAuthStatus()를 호출하고, 이후에는 즉시 반환
   */
  let resolvePromise: Promise<void> | null = null;

  const ensureAuthResolved = async ({ force = false } = {}): Promise<void> => {
    if (authResolved.value && !force) {
      return;
    }

    if (resolvePromise && !force) {
      return resolvePromise;
    }

    resolvePromise = checkAuthStatus()
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        resolvePromise = null;
      });

    return resolvePromise as Promise<void>;
  };

  const logout = async () => {
    if (isLoggingOut.value) return;
    isLoggingOut.value = true;

    try {
      if (accessToken.value) {
        try {
          await apiService.post(LOGOUT_URL, null, { _retry: true });
        } catch {
          // 서버 통보 실패 무시 (네트워크 오류, 토큰 만료 등)
        }
      }

      apiService.cancelAllRequests("Logout", true);
      clearSessionState();

      const currentPath = router.currentRoute.value.path;
      const loginPath = currentPath.startsWith("/admin") ? "/admin/login" : "/login";
      await router.replace(loginPath);
    } finally {
      isLoggingOut.value = false;
    }
  };

  const clearSessionState = () => {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }

    accessToken.value = null;
    refreshToken.value = null;
    user.value = null;
    authResolved.value = false;

    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userName");
    localStorage.removeItem("userPhone");
  };

  // 다중 탭 동기화: 다른 탭에서 로그아웃/토큰 갱신 시 현재 탭 상태 동기화
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key !== "refreshToken") return;

    if (!event.newValue) {
      if (accessToken.value) {
        clearSessionState();
        const currentPath = router.currentRoute.value.path;
        if (currentPath.startsWith("/admin")) {
          router.replace("/admin/login");
        }
      }
    } else if (event.newValue !== refreshToken.value) {
      refreshToken.value = event.newValue;
    }
  };

  window.addEventListener("storage", handleStorageChange);

  return {
    accessToken,
    refreshToken,
    user,
    isLoading,
    authResolved,
    isAuthenticated,
    isAdmin,
    isPartner,
    setAccessToken,
    setTokens,
    login,
    logout,
    checkAuthStatus,
    ensureAuthResolved,
    clearSessionState
  };
});
